import type { EnvironmentalClassBreakdownItem } from '@siliconx/shared';
import { formatTonnes } from '../format';

/**
 * SIL-476 — "Footprint by asset class" stacked bars (per
 * `Environmental Fleet Detail.dc.html`). Each row's segments are sized to
 * THAT class's own Scope 2/Scope 3 split (not scaled against a shared
 * fleet-wide max) — matching the mock's own `s2w`/`s3w` percentages, which
 * sum to 100% per row.
 */
export function ClassBreakdownBars({ items }: { items: EnvironmentalClassBreakdownItem[] }) {
  if (items.length === 0) {
    return (
      <div className="py-6 text-center text-xs text-zinc-400">No per-class footprint computed yet.</div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {items.map((r) => {
        const total = r.scope2Kgco2e + r.scope3Kgco2e;
        const scope2Pct = total > 0 ? (r.scope2Kgco2e / total) * 100 : 0;
        const scope3Pct = total > 0 ? (r.scope3Kgco2e / total) * 100 : 0;
        return (
          <div key={r.assetClassId} className="flex items-center gap-3 text-sm">
            <span className="w-28 shrink-0 truncate text-zinc-600" title={r.assetClassName}>
              {r.assetClassName}
            </span>
            <div className="flex h-3 flex-1 overflow-hidden rounded-sm bg-zinc-100">
              <div className="h-full bg-indigo-500" style={{ width: `${scope2Pct}%` }} />
              <div className="h-full bg-emerald-500" style={{ width: `${scope3Pct}%` }} />
            </div>
            <span className="w-14 shrink-0 text-right font-mono text-sm font-semibold tabular-nums">
              {formatTonnes(total)}
            </span>
          </div>
        );
      })}
      <p className="text-xs text-zinc-500">
        tCO2e · Scope 2 annualized (indigo) + Scope 3 embodied stock (emerald)
      </p>
    </div>
  );
}
